
import React, { useState, useCallback } from 'react'
import type { Canvas } from '@/hooks/useCanvas'
import type { Project } from '@/hooks/useProjects'
import type { CanvasAction } from '../context-menu'

type ModalType = 'rename' | 'move' | 'delete' | 'duplicate' | 'restore' | 'delete-permanent' | null

interface UseCanvasModalsProps {
  projects: Project[]
  onRename: (canvasId: string, newTitle: string) => Promise<unknown>
  onMove: (canvasId: string, projectId: string | null) => Promise<unknown>
  onDelete: (canvasId: string, permanent?: boolean) => Promise<unknown>
  onDuplicate: (canvasId: string, newTitle?: string) => Promise<unknown>
  onRestore?: (canvasId: string) => Promise<unknown>
  onDeletePermanent?: (canvasId: string) => Promise<unknown>
  onRemoveFromProject?: (canvasId: string) => Promise<unknown>
}

export interface UseCanvasModalsReturn {
  activeModal: ModalType
  selectedCanvas: Canvas | null
  handleAction: (action: CanvasAction, canvas: Canvas) => void
  closeModal: () => void
  modalHandlers: {
    onRename: (canvasId: string, newTitle: string) => Promise<void>
    onMove: (canvasId: string, projectId: string | null) => Promise<void>
    onDelete: (canvasId: string) => Promise<void>
    onDuplicate: (canvasId: string, newTitle: string) => Promise<void>
    onRestore: (canvasId: string) => Promise<void>
    onDeletePermanent: (canvasId: string) => Promise<void>
  }
  modalProps: {
    isRenameOpen: boolean
    isMoveOpen: boolean
    isDeleteOpen: boolean
    isDuplicateOpen: boolean
    isRestoreOpen: boolean
    isDeletePermanentOpen: boolean
    canvas: Canvas | null
    projects: Project[]
  }
}

export const useCanvasModals = ({
  projects,
  onRename,
  onMove,
  onDelete,
  onDuplicate,
  onRestore,
  onDeletePermanent,
  onRemoveFromProject,
}: UseCanvasModalsProps): UseCanvasModalsReturn => {
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  const [selectedCanvas, setSelectedCanvas] = useState<Canvas | null>(null)

  const closeModal = useCallback(() => {
    setActiveModal(null)
    setSelectedCanvas(null)
  }, [])

  const handleAction = useCallback((action: CanvasAction, canvas: Canvas) => {
    setSelectedCanvas(canvas)

    switch (action) {
      case 'rename':
        setActiveModal('rename')
        break
      case 'move':
        setActiveModal('move')
        break
      case 'delete':
        setActiveModal('delete')
        break
      case 'duplicate':
        setActiveModal('duplicate')
        break
      case 'restore':
        setActiveModal('restore')
        break
      case 'delete-permanent':
        setActiveModal('delete-permanent')
        break
      case 'remove-from-project':
        if (onRemoveFromProject) {
          onRemoveFromProject(canvas.uuid)
        }
        break
      default:
        console.warn(`Unhandled action: ${action}`)
    }
  }, [onRemoveFromProject])

  const modalHandlers = {
    onRename: useCallback(async (canvasId: string, newTitle: string) => {
      try {
        await onRename(canvasId, newTitle)
        closeModal()
      } catch (error) {
        console.error('useCanvasModals onRename failed:', error)
        throw error
      }
    }, [onRename, closeModal]),

    onMove: useCallback(async (canvasId: string, projectId: string | null) => {
      await onMove(canvasId, projectId)
      closeModal()
    }, [onMove, closeModal]),

    onDelete: useCallback(async (canvasId: string) => {
      await onDelete(canvasId)
      closeModal()
    }, [onDelete, closeModal]),

    onDuplicate: useCallback(async (canvasId: string, newTitle: string) => {
      await onDuplicate(canvasId, newTitle)
      closeModal()
    }, [onDuplicate, closeModal]),

    onRestore: useCallback(async (canvasId: string) => {
      if (onRestore) {
        await onRestore(canvasId)
        closeModal()
      }
    }, [onRestore, closeModal]),

    onDeletePermanent: useCallback(async (canvasId: string) => {
      if (onDeletePermanent) {
        await onDeletePermanent(canvasId)
        closeModal()
      }
    }, [onDeletePermanent, closeModal]),
  }

  const modalProps = {
    isRenameOpen: activeModal === 'rename',
    isMoveOpen: activeModal === 'move',
    isDeleteOpen: activeModal === 'delete',
    isDuplicateOpen: activeModal === 'duplicate',
    isRestoreOpen: activeModal === 'restore',
    isDeletePermanentOpen: activeModal === 'delete-permanent',
    canvas: selectedCanvas,
    projects,
  }


  return {
    activeModal,
    selectedCanvas,
    handleAction,
    closeModal,
    modalHandlers,
    modalProps,
  }
}